import { useQuery } from "@apollo/client/react"
import { GetCustomerActivitiesDocument } from "@/graphql/graphql"
import React from "react"
import ACTIVITIES from "@/components/activities"
import IconButton from "@mui/material/IconButton"
import { ChevronLeft, ChevronRight } from "lucide-mui"
import ActivitySkeleton from "@/components/activities/skeleton"
import { useCursorPagination } from "@/shared/hooks"
import i18n from "@/i18n"

const Activities = ({ customer, customerContact: _customerContact }) => {
  const { cursor, createNavigationFunctions } = useCursorPagination({ perPage: 10 })

  const { data, loading, error } = useQuery(GetCustomerActivitiesDocument, {
    variables: {
      customerId: customer.id,
      ...cursor,
    },
  })

  const pageInfo = data?.business?.customer?.activities?.pageInfo
  const { hasPreviousPage, hasNextPage, previousPage, nextPage } = createNavigationFunctions(pageInfo)

  if (!data && loading) {
    return (
      <div className="flex flex-col gap-4 p-2">
        <ActivitySkeleton />
      </div>
    )
  }

  if (error) return <div className="p-4 text-red-600">{i18n.t("app.clients.client_modal.activities.error")}</div>

  const activities = data?.business?.customer?.activities?.nodes || []

  const renderActivity = (activity) => {
    const Component = ACTIVITIES[activity.key]

    if (Component) {
      return (
        <Component
          key={activity.id}
          activity={activity}
          className="border-0 border-solid border-b border-b-gray-100 px-6"
        />
      )
    }

    return (
      <div key={activity.id} className="p-4 text-gray-500 text-sm">
        Unknown activity: {activity.key}
      </div>
    )
  }

  return (
    <div data-testid="panel-clients-modal-activities-container" className="flex flex-col h-full">
      <div className="flex flex-col flex-1">
        {activities.length === 0 ? (
          <div
            data-testid="panel-clients-modal-activities-empty"
            className="flex items-center justify-center p-8 text-gray-500"
          >
            {i18n.t("app.clients.client_modal.activities.empty")}
          </div>
        ) : (
          activities.map(renderActivity)
        )}
      </div>

      <div data-testid="panel-clients-modal-activities-pagination" className="flex justify-between w-full px-5 py-2">
        {hasPreviousPage && (
          <IconButton data-testid="panel-clients-modal-activities-prev" onClick={previousPage}>
            <ChevronLeft />
          </IconButton>
        )}
        {hasNextPage && (
          <IconButton data-testid="panel-clients-modal-activities-next" onClick={nextPage} className="ml-auto">
            <ChevronRight />
          </IconButton>
        )}
      </div>
    </div>
  )
}

export default Activities
